import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, MessageSquare, AlertCircle, Timer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type MsgRow = {
  id: string;
  deal_id: string;
  direction: 'in' | 'out';
  channel: 'email' | 'sms';
  rep_id: string | null;
  created_at: string;
  sent_at: string | null;
  received_at: string | null;
  deals: { customer_name: string | null; rep_id: string | null } | null;
};

function fmtMins(mins: number): string {
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = mins / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

const STALE_HOURS = 2; // awaiting reply threshold

export function ConversationsInsightsCard() {
  const { mode, dealershipId } = useAuth();
  const [rows, setRows] = useState<MsgRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mode !== 'real' || !dealershipId) return;
    let mounted = true;
    (async () => {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('messages')
        .select('id, deal_id, direction, channel, rep_id, created_at, sent_at, received_at, deals(customer_name, rep_id)')
        .eq('dealership_id', dealershipId)
        .gte('created_at', since)
        .order('created_at', { ascending: true })
        .limit(1000);
      if (mounted) {
        setRows((data ?? []) as unknown as MsgRow[]);
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [mode, dealershipId]);

  if (mode !== 'real') return null;

  // Group by deal
  const byDeal = new Map<string, MsgRow[]>();
  for (const r of rows) {
    const arr = byDeal.get(r.deal_id) ?? [];
    arr.push(r);
    byDeal.set(r.deal_id, arr);
  }

  // Compute response times: for every inbound followed by an outbound from the rep, delta = out - in
  const responseDeltasMin: number[] = [];
  const awaiting: { deal_id: string; customer: string; waitedMin: number }[] = [];

  for (const [deal_id, msgs] of byDeal) {
    let pendingInboundAt: number | null = null;
    for (const m of msgs) {
      const ts = new Date(m.received_at ?? m.sent_at ?? m.created_at).getTime();
      if (m.direction === 'in') {
        if (pendingInboundAt === null) pendingInboundAt = ts;
      } else if (m.direction === 'out' && pendingInboundAt !== null) {
        responseDeltasMin.push((ts - pendingInboundAt) / 60000);
        pendingInboundAt = null;
      }
    }
    if (pendingInboundAt !== null) {
      const waitedMin = (Date.now() - pendingInboundAt) / 60000;
      if (waitedMin >= STALE_HOURS * 60) {
        const last = msgs[msgs.length - 1];
        awaiting.push({
          deal_id,
          customer: last.deals?.customer_name ?? 'Customer',
          waitedMin,
        });
      }
    }
  }
  awaiting.sort((a, b) => b.waitedMin - a.waitedMin);

  const avgResponseMin = responseDeltasMin.length > 0
    ? responseDeltasMin.reduce((s, n) => s + n, 0) / responseDeltasMin.length
    : null;
  const activeThreads = byDeal.size;
  const awaitingCount = awaiting.length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" /> Conversations
          <span className="ml-auto text-xs text-muted-foreground font-normal">Last 7d</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-md bg-muted/40 p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              <Timer className="h-3 w-3" /> Avg reply
            </div>
            <div className="text-lg font-bold mt-1">
              {avgResponseMin == null ? '—' : fmtMins(avgResponseMin)}
            </div>
          </div>
          <div className="rounded-md bg-muted/40 p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              <MessageSquare className="h-3 w-3" /> Active
            </div>
            <div className="text-lg font-bold mt-1">{activeThreads}</div>
          </div>
          <div className={`rounded-md p-3 ${awaitingCount > 0 ? 'bg-red-500/10' : 'bg-muted/40'}`}>
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wide ${awaitingCount > 0 ? 'text-red-300' : 'text-muted-foreground'}`}>
              <AlertCircle className="h-3 w-3" /> Awaiting
            </div>
            <div className={`text-lg font-bold mt-1 ${awaitingCount > 0 ? 'text-red-300' : ''}`}>{awaitingCount}</div>
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-muted-foreground text-center py-2">Loading…</p>
        ) : activeThreads === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">
            No customer messages yet. Once reps connect their inbox or number, threads land here.
          </p>
        ) : awaiting.length > 0 ? (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
              Unanswered &gt; {STALE_HOURS}h
            </p>
            <div className="space-y-1.5">
              {awaiting.slice(0, 5).map(a => (
                <Link
                  key={a.deal_id}
                  to={`/deals/${a.deal_id}`}
                  className="flex items-center gap-2 p-2 rounded hover:bg-muted/40 transition-colors"
                >
                  <Clock className="h-3.5 w-3.5 text-red-400 shrink-0" />
                  <span className="text-sm font-medium truncate flex-1">{a.customer}</span>
                  <span className="text-xs text-red-300 shrink-0">waited {fmtMins(a.waitedMin)}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-emerald-400 text-center py-2">All customer messages answered.</p>
        )}
      </CardContent>
    </Card>
  );
}